import type { SolutionHomeCardDto, SolutionHomeCardStepStatus, SolutionHomeCardStepStatuses } from '../dto/solution.dto';
import { genderDisplayText } from '@/shared/enums/gender';
import { solutionStatusText } from '@/shared/enums/solution-status';
import { normalizeSolutionType } from '@/shared/enums/solution-type';

type SolutionRecord = {
  id: number;
  name?: string;
  case_maindata_id?: number;
  case_maindata?: { id: number; name?: string; [key: string]: unknown };
  uuid?: string;
  version?: number;
  status?: string;
  factor?: { name?: string; title?: string; [key: string]: unknown };
  ga_factor?: { name?: string; title?: string; [key: string]: unknown };
  gaFactor?: { name?: string; title?: string; [key: string]: unknown };
  factor_name?: string;
  updatedAt?: string;
  updated_at?: string;
  createdAt?: string;
  created_at?: string;
  mdps_production_order?: unknown;
  [key: string]: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function pickName(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (!isRecord(value)) return '';
  for (const key of ['fullname', 'show_name', 'name', 'title', 'label']) {
    const field = value[key];
    if (typeof field === 'string' && field) return field;
    if (field != null && typeof field !== 'object') return String(field);
  }
  return '';
}

function firstPick(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!obj) return '';
  for (const key of keys) {
    const value = pickName(obj[key]);
    if (value) return value;
  }
  return '';
}

function genderText(raw: unknown): string {
  return genderDisplayText(raw);
}

function formatUpdatedAt(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const dateText = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(raw) ? raw.replace(/\s+/, 'T') : raw;
  const date = new Date(dateText);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs >= 0 && diffMs < 86400000) {
    return `${Math.max(1, Math.floor(diffMs / 3600000))}小时前`;
  }
  const pad2 = (n: number): string => String(n).padStart(2, '0');
  const MM = pad2(date.getMonth() + 1);
  const DD = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mm = pad2(date.getMinutes());
  if (date.getFullYear() === now.getFullYear()) {
    return `${MM}月${DD}日 ${hh}:${mm}`;
  }
  return `${date.getFullYear()}-${MM}-${DD} ${hh}:${mm}`;
}

function solutionTimeRaw(row: SolutionRecord): unknown {
  for (const key of ['updatedAt', 'updated_at', 'createdAt', 'created_at']) {
    const value = row[key];
    if (value != null && value !== '') return value;
  }
  return undefined;
}

function solutionGroupKey(row: SolutionRecord): string {
  return row.uuid ? `uuid:${row.uuid}` : `id:${row.id || 0}`;
}

function solutionVersion(row: SolutionRecord): number {
  return Number(row.version) > 0 ? Number(row.version) : 0;
}

function toTimestamp(value: unknown): number {
  const raw = String(value ?? '').trim();
  if (!raw) return 0;
  const dateText = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(raw) ? raw.replace(/\s+/, 'T') : raw;
  const date = new Date(dateText);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function isNewerSolution(a: SolutionRecord, b: SolutionRecord): boolean {
  if (solutionVersion(a) !== solutionVersion(b)) return solutionVersion(a) > solutionVersion(b);
  const ta = toTimestamp(solutionTimeRaw(a));
  const tb = toTimestamp(solutionTimeRaw(b));
  if (ta !== tb) return ta > tb;
  return a.id > b.id;
}

function dedupeLatestSolution(rows: SolutionRecord[]): SolutionRecord[] {
  const map = new Map<string, SolutionRecord>();
  for (const row of rows) {
    const key = solutionGroupKey(row);
    const prev = map.get(key);
    if (!prev || isNewerSolution(row, prev)) {
      map.set(key, row);
    }
  }
  return [...map.values()];
}

function sortSolutionForHome(a: SolutionRecord, b: SolutionRecord): number {
  const statusOrder: Record<string, number> = { design: 0, draft: 1, madeing: 2 };
  const sd = (statusOrder[String(a.status ?? '')] ?? 999) - (statusOrder[String(b.status ?? '')] ?? 999);
  if (sd !== 0) return sd;
  const td = toTimestamp(solutionTimeRaw(b)) - toTimestamp(solutionTimeRaw(a));
  if (td !== 0) return td;
  const vd = solutionVersion(b) - solutionVersion(a);
  return vd !== 0 ? vd : b.id - a.id;
}

function caseMainId(row: SolutionRecord): number {
  return Number(row.case_maindata_id ?? row.case_maindata?.id ?? 0) || 0;
}

function extractArchive(row: unknown): Record<string, unknown> | null {
  if (!isRecord(row)) return null;
  const raw = row.extend ?? row.case_maindata_archive ?? row.caseMaindataArchive ?? row.archive ?? row.case_archive ?? row.caseMaindataArchiveRecord;
  const archive = Array.isArray(raw) ? raw[0] : raw;
  return isRecord(archive) ? archive : null;
}

function factorNames(caseMain: Record<string, unknown> | null): string[] {
  const list = Array.isArray(caseMain?.factors) ? caseMain.factors : [];
  return list
    .map(item => pickName(item))
    .filter(Boolean);
}

function patientOrSolutionTitle(patientName: string, row: SolutionRecord, factorName: string): string {
  const solutionTitle = row.name?.trim() || `方案 #${row.id}`;
  const base = patientName || solutionTitle;
  return factorName ? `${base}-${factorName}` : base;
}

function relationName(caseMain: Record<string, unknown> | null | undefined, key: string): string {
  return caseMain ? pickName(caseMain[key]) : '';
}

function joinText(parts: string[], separator: string): string {
  return parts.map(part => part.trim()).filter(Boolean).join(separator);
}

function mdpsOrderStatus(row: SolutionRecord): string {
  const raw = row.mdps_production_order;
  const order = Array.isArray(raw) ? raw[0] : raw;
  return isRecord(order) ? String(order.status ?? '').trim() : '';
}

function pickPrimaryTask(row: SolutionRecord): Record<string, unknown> | null {
  const raw = row.tasks;
  if (Array.isArray(raw)) {
    const first = raw[0];
    return isRecord(first) ? first : null;
  }
  return isRecord(raw) ? raw : null;
}

function solutionStepStatusesFromTaskStatus(
  taskStatus: string,
): [SolutionHomeCardStepStatus, SolutionHomeCardStepStatus] {
  if (taskStatus === 'completed') return ['done', 'done'];
  if (taskStatus === 'processing') return ['working', 'todo'];
  return ['todo', 'todo'];
}

function solutionStepStatuses(row: SolutionRecord): [SolutionHomeCardStepStatus, SolutionHomeCardStepStatus] {
  return solutionStepStatusesFromTaskStatus(primaryTaskStatus(row));
}

function primaryTaskStatus(row: SolutionRecord): string {
  const task = pickPrimaryTask(row);
  return task ? String(task.status ?? '').trim() : '';
}

function mdpsStepStatuses(row: SolutionRecord): [SolutionHomeCardStepStatus, SolutionHomeCardStepStatus] {
  const mdpsStatus = mdpsOrderStatus(row);
  if (mdpsStatus === 'finished') return ['done', 'working'];
  if (mdpsStatus === 'in_progress') return ['working', 'todo'];
  return ['todo', 'todo'];
}

function homeStepStatuses(row: SolutionRecord): SolutionHomeCardStepStatuses {
  return [
    ...solutionStepStatuses(row),
    ...mdpsStepStatuses(row),
  ];
}

function buildWorkspaceCard(row: SolutionRecord, archive: Record<string, unknown> | null): SolutionHomeCardDto {
  const status = String(row.status ?? '');
  const patientName = typeof archive?.name === 'string' ? archive.name : '';
  const caseMain = isRecord(row.case_maindata) ? row.case_maindata : null;
  const caseStatus = String(caseMain?.status ?? '').trim();
  const factors = factorNames(caseMain);
  const factorName = factors[0] ?? '';
  const title = patientOrSolutionTitle(patientName, row, factorName);
  const hospital = relationName(caseMain, 'hospital');
  const doctor = relationName(caseMain, 'doctor');
  return {
    id: row.id,
    solutionId: row.id,
    caseId: caseMainId(row) || null,
    title,
    status: solutionStatusText(status),
    caseStatus,
    solutionStatus: status,
    taskStatus: primaryTaskStatus(row),
    statusColor: status === 'draft' || status === 'cancel' ? 'slate' : 'green',
    stepStatuses: homeStepStatuses(row),
    patientName,
    ageText: archive?.age != null && archive.age !== '' ? String(archive.age) : '',
    genderText: genderText(archive?.gender),
    hospitalDoctorText: joinText([hospital, doctor], '/'),
    orderCenter: relationName(caseMain, 'site'),
    indication: archive ? firstPick(archive, ['indication', 'ga_indication', 'indicationInfo']) : '',
    treatmentPlan: archive ? firstPick(archive, ['treatment_plan', 'ga_treatment_plan', 'treatmentPlan']) : '',
    factorName,
    factors,
    versionText: `V${solutionVersion(row) || 1}`,
    updatedAt: formatUpdatedAt(solutionTimeRaw(row)),
    solutionType: normalizeSolutionType(row.type),
  };
}

export class SolutionHomeCardMapper {
  static async toHomeCardList(
    rows: unknown[],
    loadArchivesByIds: (ids: number[]) => Promise<unknown[]>,
  ): Promise<SolutionHomeCardDto[]> {
    const normalized = (Array.isArray(rows) ? rows : []).filter(isRecord) as SolutionRecord[];
    const deduped = dedupeLatestSolution(normalized);
    deduped.sort(sortSolutionForHome);

    const byId = new Map<number, Record<string, unknown>>();
    const need: number[] = [];

    for (const row of deduped) {
      const cid = caseMainId(row);
      if (!cid) continue;
      const archive = extractArchive(row.case_maindata) ?? extractArchive(row);
      if (archive) {
        byId.set(cid, archive);
      } else {
        need.push(cid);
      }
    }

    const missing = [...new Set(need)].filter(id => id > 0);
    if (missing.length > 0) {
      try {
        const caseRows = await loadArchivesByIds(missing);
        if (Array.isArray(caseRows)) {
          for (const row of caseRows) {
            if (!isRecord(row)) continue;
            const id = Number(row.id);
            if (id <= 0) continue;
            const archive = extractArchive(row);
            if (archive) {
              byId.set(id, archive);
            }
          }
        }
      } catch {
        // ignore archive lookup failures
      }
    }

    return deduped.map(row => buildWorkspaceCard(row, caseMainId(row) ? byId.get(caseMainId(row)) ?? null : null));
  }
}
